export const IMAGE_WEBP_MIME = 'image/webp';
export const IMAGE_WEBP_EXT = 'webp';
export const DEFAULT_WEBP_QUALITY = 0.8;

export type CropAreaPixels = { x: number; y: number; width: number; height: number };

export const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const createHtmlImage = (src: string, useCors = false): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', () => reject(new Error('이미지 로드 실패')));
    if (useCors) {
      image.setAttribute('crossOrigin', 'anonymous');
    }
    image.src = src;
  });

const canvasToWebpBlob = (canvas: HTMLCanvasElement, quality = DEFAULT_WEBP_QUALITY): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('WebP 변환 실패'))),
      IMAGE_WEBP_MIME,
      quality
    );
  });

const drawCroppedCanvas = async (
  imageSrc: string,
  cropPixels: CropAreaPixels,
  useCors = false
): Promise<HTMLCanvasElement> => {
  const isDataUrl = imageSrc.startsWith('data:');
  const image = await createHtmlImage(imageSrc, useCors && !isDataUrl);
  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = Math.max(1, Math.floor(cropPixels.width));
  cropCanvas.height = Math.max(1, Math.floor(cropPixels.height));
  const cropCtx = cropCanvas.getContext('2d');
  if (!cropCtx) throw new Error('canvas context 생성 실패');
  cropCtx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    cropCanvas.width,
    cropCanvas.height
  );
  return cropCanvas;
};

/** 크롭 후 긴 변 maxLongEdge로 리사이즈하여 WebP Blob 반환 */
export const cropToWebpBlob = async (
  imageSrc: string,
  cropPixels: CropAreaPixels,
  maxLongEdge = 1280,
  quality = DEFAULT_WEBP_QUALITY
): Promise<Blob> => {
  const cropCanvas = await drawCroppedCanvas(imageSrc, cropPixels);
  const longEdge = Math.max(cropCanvas.width, cropCanvas.height);
  const scale = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1;
  const outW = Math.max(1, Math.round(cropCanvas.width * scale));
  const outH = Math.max(1, Math.round(cropCanvas.height * scale));

  const outCanvas = document.createElement('canvas');
  outCanvas.width = outW;
  outCanvas.height = outH;
  const outCtx = outCanvas.getContext('2d');
  if (!outCtx) throw new Error('canvas context 생성 실패');
  outCtx.imageSmoothingEnabled = true;
  outCtx.imageSmoothingQuality = 'high';
  outCtx.drawImage(cropCanvas, 0, 0, outW, outH);

  return canvasToWebpBlob(outCanvas, quality);
};

/** 크롭 후 정사각형 outputSize로 리사이즈하여 WebP Blob 반환 */
export const cropSquareToWebp = async (
  imageSrc: string,
  cropPixels: CropAreaPixels,
  outputSize: number,
  quality = DEFAULT_WEBP_QUALITY,
  useCors = false
): Promise<Blob> => {
  const cropCanvas = await drawCroppedCanvas(imageSrc, cropPixels, useCors);
  const outCanvas = document.createElement('canvas');
  outCanvas.width = outputSize;
  outCanvas.height = outputSize;
  const outCtx = outCanvas.getContext('2d');
  if (!outCtx) throw new Error('canvas context 생성 실패');
  outCtx.imageSmoothingEnabled = true;
  outCtx.imageSmoothingQuality = 'high';
  outCtx.drawImage(cropCanvas, 0, 0, outputSize, outputSize);
  return canvasToWebpBlob(outCanvas, quality);
};

/** 크롭 후 maxWidth(또는 정사각형)로 리사이즈하여 WebP Blob 반환 */
export const cropToMaxWidthWebp = async (
  imageSrc: string,
  cropPixels: CropAreaPixels,
  maxWidth: number,
  options?: { quality?: number; isSquare?: boolean; useCors?: boolean }
): Promise<Blob> => {
  const quality = options?.quality ?? DEFAULT_WEBP_QUALITY;
  const cropCanvas = await drawCroppedCanvas(imageSrc, cropPixels, options?.useCors ?? false);

  let targetW: number;
  let targetH: number;
  if (options?.isSquare) {
    targetW = maxWidth;
    targetH = maxWidth;
  } else {
    targetW = Math.min(maxWidth, cropCanvas.width);
    targetH = Math.max(1, Math.round((targetW / cropCanvas.width) * cropCanvas.height));
  }

  const outCanvas = document.createElement('canvas');
  outCanvas.width = targetW;
  outCanvas.height = targetH;
  const outCtx = outCanvas.getContext('2d');
  if (!outCtx) throw new Error('canvas context 생성 실패');
  outCtx.imageSmoothingEnabled = true;
  outCtx.imageSmoothingQuality = 'high';
  outCtx.drawImage(cropCanvas, 0, 0, targetW, targetH);
  return canvasToWebpBlob(outCanvas, quality);
};

/** 파일을 리사이즈·압축하여 WebP Blob으로 변환 (크롭 없음) */
export const fileToWebpBlob = async (
  file: File,
  maxLongEdge = 1920,
  quality = DEFAULT_WEBP_QUALITY
): Promise<Blob> => {
  const dataUrl = await readFileAsDataURL(file);
  const image = await createHtmlImage(dataUrl);
  const longEdge = Math.max(image.width, image.height);
  const scale = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1;
  const w = Math.max(1, Math.round(image.width * scale));
  const h = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas context 생성 실패');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, 0, 0, w, h);
  return canvasToWebpBlob(canvas, quality);
};
